import { Button, Snackbar } from '@material-ui/core';
import React, { Component, Fragment, MouseEvent, RefObject } from 'react';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { faAlignLeft, faIndent } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ViewItem } from './Viewer';
import xmlJs from 'xml-js';

const styles = (theme: Theme) => createStyles({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: '1rem',
  },
  nestedBlock: {
    display: 'block',
    paddingLeft: '2rem',
    borderLeft: '1px solid #ccc',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  buttonLeft: {
    marginRight: theme.spacing(1)
  },
  key: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: '#905',
  },
  value: {
    fontFamily: 'Consolas',
    padding: "0 0.3rem",
    borderRadius: "0.3rem",
    "&:hover": {
      backgroundColor: "#eee",
      cursor: "pointer"
    }
  },
})

type State = {
  notifyCopy: boolean
  flatten: boolean
}

class JsonViewer extends Component<Props, State> {
  contentRef = React.createRef<HTMLDivElement>()

  state = {
    notifyCopy: false,
    flatten: false
  }

  componentDidMount() {
    this.props.contentConsumer(this.contentRef)
  }

  liftText = (obj: { [key: string]: any }): (string | any) => {
    let props: { [s: string]: any } = {}

    if ("_text" in obj) {
      return obj['_text']
    }

    _.forOwn(obj, (value, key) => {
      // remove namespace
      key = key.substr(key.indexOf(":") + 1)
      props[key] = this.liftText(value)
    })
    return props
  }

  handleCopy = (e: MouseEvent<HTMLElement>) => {
    this.props.copyFunction(e.currentTarget)
    this.setState({
      notifyCopy: true
    })
  }

  renderObj(obj: any, nested: boolean = true) {
    const { classes } = this.props

    switch (typeof obj) {
      case 'boolean':
      case 'number':
      case 'string':
        return <code className={classes.value} onClick={this.handleCopy} title="Click to copy">{obj}</code>
      default:
        let output: JSX.Element[] = []
        _.forOwn(obj, (v, k) => {
          let child = this.renderObj(v)

          if (this.state.flatten) {
            if (Array.isArray(child))
              output.push(<Fragment key={k}>{child}</Fragment>)
            else
              output.push(<div key={k}><span className={classes.key}>{k}: </span>{child}</div>)
          }
          else
            output.push(<div className={nested ? classes.nestedBlock : undefined} key={k}><span className={classes.key}>{k}: </span>{child}</div>)
        })
        return output
    }
  }

  render() {
    const { item, classes } = this.props

    let json = {}

    if (item.class === "message") {
      try {
        json = xmlJs.xml2js(item.body, {
          compact: true,
          ignoreAttributes: true,
          ignoreDeclaration: true,
          ignoreInstruction: true,
          ignoreComment: true,
          ignoreCdata: true,
          ignoreDoctype: true,
          nativeType: true,
        })
        json = this.liftText(json).Envelope.Body
      } catch(e) {
        return <div>{e.toString()}</div>
      }
    }
    else if (item.class === "sdkaction")
      json = item
    else
      return <div>Unknown record type</div>

    return (
      <div className={classes.root} ref={this.contentRef}>
        <div className={classes.toolbar}>
          <Button variant="contained" color="default" onClick={() => { this.setState({ flatten: !this.state.flatten }) }}>
            <FontAwesomeIcon icon={this.state.flatten ? faIndent : faAlignLeft} className={classes.buttonLeft} />
            {this.state.flatten ? "Nested View" : "Flat View"}
          </Button>
        </div>
        {this.renderObj(json, false)}
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          open={this.state.notifyCopy}
          autoHideDuration={2000}
          onClose={() => this.setState({ notifyCopy: false })}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">Copied to clipboard</span>} />
      </div>
    )
  }
}

interface Props extends WithStyles<typeof styles> {
  contentConsumer(ref: RefObject<HTMLElement>): void
  copyFunction(e: HTMLElement): void
  item: ViewItem
}

export default withStyles(styles)(JsonViewer)